import * as React from "react";
import {useContext} from "react";
import {
    PricingTier, StripeCancelSubscriptionsRequest,
    StripeCreateCheckoutSessionRequest,
    StripeCreateCustomerRequest,
    SubscribePageProps,
} from "../utils/typesStripe";
import {
    SubscriptionTier, subscriptionTierFromDisplayName
} from "../utils/types";
import AppTheme from "../themes/AppTheme";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import AppAppBar from "../components/AppAppBar";
import Footer from "../components/Footer";
import {AuthContext} from "../components/AuthContext";
import {EmbeddedCheckout, EmbeddedCheckoutProvider} from '@stripe/react-stripe-js';
import {loadStripe} from "@stripe/stripe-js";
import {cancelAllStripeSubscriptions, createStripeCheckoutSession, createStripeCustomerAccount} from "../apis/junkyard";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Pricing from "../components/Pricing";
import {useLocation} from 'react-router';


export const SubscribePage: React.FC<SubscribePageProps> = (props) => {

    const authContext = useContext(AuthContext);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const stripe_publishable_key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||'pk_live_51TDrNK1SiofXQTOkc9mrrE71jC7voiug5xdntD1fZl8ycVrGXWOuiA0IySrjExzBKz71wcxqn8VzJYWlnRXXOBi600ewvdTr9S';
    const stripePromise = loadStripe(stripe_publishable_key);
    const [showCheckout, setShowCheckout] = React.useState<boolean>(false);
    const [clientSecret, setStripeClientSecret] = React.useState<string | null>();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const [sessionId, setStripeSessionId] = React.useState<string | null>(params.get('session_id'));
    const currentSubscriptionTier = authContext.getSubscriptionTier();
    const [selectedTier, setSelectedTier] = React.useState<SubscriptionTier>(currentSubscriptionTier);

    // Customer information form state
    const [customerInfo, setCustomerInfo] = React.useState({
        name: '',
        email: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'US',
    });

    const createStripeCustomer = async () => {
        try {
            const checkoutSession = await createStripeCustomerAccount(
                {
                    customer_name: customerInfo.name, // the API will ignore this
                    access_token: authContext.readAccessToken(),
                } as StripeCreateCustomerRequest
            )
            return checkoutSession.stripe_customer.id;
        }
        catch (error) {
            console.error("Failed to get stripe checkout session client secret", error);
        }
    }

    const getClientSecret = async (customerId: string) => {
        try {
            const checkoutSession = await createStripeCheckoutSession(
                {
                    "access_token": authContext.readAccessToken(),
                    "stripe_customer_id": customerId,
                    "subscription_tier": selectedTier,

                } as StripeCreateCheckoutSessionRequest
            )
            setIsLoading(false)
            return checkoutSession.client_secret;
        }
        catch (error) {
            console.error("Failed to get stripe checkout session client secret", error);
        }

    }

    const initStripe = async () => {

        if (stripe_publishable_key === process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
            console.log("Stripe publishable key overridden from env var!")
        }
        setIsLoading(true);
        createStripeCustomer().then((_customerId) => {
            if (_customerId) {
                getClientSecret(_customerId).then((_clientSecret) => {
                    if (_clientSecret) {
                        setStripeClientSecret(_clientSecret)
                        setShowCheckout(true);
                        setIsLoading(false);
                    } else {
                        console.error("No client secret")
                        setIsLoading(false);
                    }
                })
            } else {
                console.error("No customer id")
                setIsLoading(false);
            }
        });
    }

    const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setCustomerInfo(prev => ({
            ...prev,
            [field]: event.target.value
        }));
    };

    const handleSubmitCustomerInfo = (event: React.FormEvent) => {
        event.preventDefault();
        initStripe();
    };

    const handleSelectedPricingTier = (sTier: PricingTier): void => {
        setSelectedTier(subscriptionTierFromDisplayName(sTier.title));
    }

    const handleCancelSubscriptions = async () => {

        const r = {
            "access_token": authContext.readAccessToken()
        } as StripeCancelSubscriptionsRequest;

        try {
            let result = await cancelAllStripeSubscriptions(r);
            if (result.num_cancel_failures > 0) {
                console.error(`Failed to cancel ${result.num_cancel_failures} subscriptions`)
            }
            if (result.num_cancel_successes === 0) {
                console.error(`Cancelled ${result.num_cancel_successes} subscriptions`)
            }
        } catch (error) {
            console.error("Failed to cancel all subscriptions")

        }
    }


    return (
        (
            <React.Fragment>
                <AppTheme>
                    <CssBaseline enableColorScheme />
                    <Box sx={{
                        mt: 5,
                        display: 'flex',
                        justifyContent: 'center',
                        width: "99%",
                        minHeight: "80vh"
                    }}>
                        <AppAppBar></AppAppBar>

                        <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 2
                        }}
                        >
                            {(!showCheckout || !clientSecret) &&
                                <Pricing
                                    showButtons={false}
                                    selectable={true}
                                    onSelect={handleSelectedPricingTier}
                                />
                            }

                            {!authContext.isAuthenticated() ? (
                                <Box sx={{margin: 20, display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                                    <Typography variant="h6">Please sign in to subscribe</Typography>
                                </Box>
                            ) : isLoading ? (
                                <Box sx={{margin: 20, display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                                    <CircularProgress />
                                    <Typography sx={{mt: 2}}>Setting up checkout...</Typography>
                                </Box>
                            ) : showCheckout && clientSecret ? (
                                <Box sx={{mt: 20, width: '70vw'}}>
                                    <EmbeddedCheckoutProvider
                                        stripe={stripePromise}
                                        options={{clientSecret: clientSecret}}
                                    >
                                        <EmbeddedCheckout/>
                                    </EmbeddedCheckoutProvider>
                                </Box>
                            ) : (
                                <Box component="form" onSubmit={handleSubmitCustomerInfo} sx={{mt: 10, height: 80}}>
                                    <Grid container spacing={2}>
                                        <Grid size={12}>
                                            {selectedTier && selectedTier !== SubscriptionTier.free && selectedTier !== currentSubscriptionTier && (
                                            <Button
                                                type="submit"
                                                fullWidth
                                                variant="contained"
                                                size="medium"
                                                onClick={initStripe}
                                            >
                                                Continue to Payment
                                            </Button>
                                        )}
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}
                        </Box>
                    </Box>
                    <Footer />
                </AppTheme>
            </React.Fragment>
        )
    )

}
